import { CheckIcon } from '@heroicons/react/20/solid'
import HeroTitle from '#app/components/hero-title.tsx'
import { Footer } from '#app/components/footer.tsx'
import React from 'react'
import classNames from 'classnames'
import { Link } from '@remix-run/react'

const _products = {
	tiers: [
		{
			name: '2 Week Free Trial',
			key: 'free_trial',
			price: { amount: '$0', frequency: '2 week free trial' },
			description: 'Simple, predictable monthly pricing.',
			features: [
				'Unlimited clients and returns',
				'Client portal included (refundr.ca)',
				'All features included',
				'Unlimited use of all communication tools',
				'Dedicated human support',
			],
			isDefault: true,
		},
		{
			name: 'Team Member Pricing',
			key: 'account_owner_subscription',
			price: { amount: '$49.99', frequency: 'month per team member' },
			description: 'Simple, predictable monthly pricing.',
			features: [
				'Add as many team members as required',
				'Unlimited clients and returns',
				'All features included',
				'Client portal included (refundr.ca)',
				'Unlimited use of all communication tools',
				'Advanced analytics',
				'Dedicated human support',
			],
			isDefault: false,
		},
	],
}

export default function PricingRoute() {
	return (
		<div className="bg-white">
			<div>
				{/* Pricing section */}
				<form className="group/tiers bg-white">
					<div className="mx-auto max-w-7xl px-6 lg:px-8">
						<HeroTitle title="Member Pricing" subject="Pricing" showSubject={false} description="Designed to connect tax professionals — and their clients — with the best experience yet." />
						<div className="isolate mx-auto mt-10 grid max-w-md grid-cols-1 gap-8 lg:max-w-4xl lg:grid-cols-2 xl:mx-auto">
							{_products.tiers.map(tier => (
								<div key={tier.key} className={classNames('group/tier rounded-3xl p-8 ring-1 ring-gray-300', { 'ring-2 ring-indigo-600': tier.isDefault })}>
									<div className="flex items-center justify-between gap-x-4">
										<h3 id={`tier-${tier.key}`} className={classNames('text-lg/8 font-semibold text-gray-900', { 'text-indigo-600': tier.isDefault })}>
											{tier.name}
										</h3>
									</div>
									<div className="mt-4 text-sm/6 text-gray-600">
										{tier.description} {tier.key === 'free_trial' && <div className="text-sm font-bold">no credit card required</div>}
										<div className="my-6 flex items-baseline gap-x-1">
											{tier.key === 'account_owner_subscription' ? (
												<>
													<span className="text-2xl font-semibold tracking-tight text-gray-900 text-red-600 line-through">$59.99</span>
													<span className="ml-2 text-2xl font-semibold tracking-tight text-gray-900">{tier.price.amount}</span>
													<span className="ml-1 text-sm/6 font-semibold text-gray-600">/{tier.price.frequency}</span>
												</>
											) : (
												<>
													<span className="text-3xl font-semibold tracking-tight text-gray-900">{tier.price.amount}</span>
													<span className="text-sm/6 font-semibold text-gray-600">/{tier.price.frequency}</span>
												</>
											)}
										</div>
										<div className="gap-x-1 text-center font-bold text-indigo-600">
											{tier.key === 'account_owner_subscription' && <div>Get Ahead of Tax Season — With a Better Deal!</div>}
										</div>
									</div>

									{tier.key === 'free_trial' && (
										<div>
											<div>
												<Link to="/signup" className="my-8 block w-full rounded bg-blue-600 px-3 py-3 text-center text-sm font-semibold text-white">
													Get Started For Free
												</Link>
											</div>
										</div>
									)}

									{tier.key === 'account_owner_subscription' && (
										<div>
											<div>
												<button disabled className="my-8 block w-full cursor-not-allowed rounded bg-gray-400 px-3 py-3 text-center text-sm font-semibold text-white opacity-50">
													Purchase Subscription
												</button>
											</div>
										</div>
									)}

									{tier.key === 'team_member_seat' && (
										<div>
											<div>
												<button disabled className="my-8 block w-full cursor-not-allowed rounded bg-gray-400 px-3 py-3 text-center text-sm font-semibold text-white opacity-50">
													Add Team Member Seat
												</button>
											</div>
										</div>
									)}

									{/* OPTIONS */}
									<ul role="list" className="space-y-3 text-sm/6 text-gray-600">
										{tier.features.map(feature => (
											<li key={feature} className="flex gap-x-3">
												<CheckIcon aria-hidden="true" className="h-6 w-5 flex-none text-indigo-600" />
												{feature}
											</li>
										))}
									</ul>
								</div>
							))}
						</div>
					</div>
				</form>
				<div className="bg-white pb-16 pt-24 sm:pb-24 sm:pt-24 xl:pb-32">
					<div className="bg-gray-900 pb-20 sm:pb-24 xl:pb-0">
						<div className="mx-auto flex max-w-7xl flex-col items-center gap-x-8 gap-y-10 px-6 sm:gap-y-8 lg:px-8 xl:flex-row xl:items-stretch">
							<div className="-mt-8 w-full max-w-2xl xl:-mb-8 xl:w-96 xl:flex-none">
								<div className="relative h-full md:-mx-8 xl:mx-0 xl:aspect-auto">
									<img alt="" src="/img/logo/jpts.png" className="absolute inset-0 size-full rounded-2xl bg-gray-800 shadow-2xl" />
								</div>
							</div>
							<div className="w-full max-w-2xl xl:max-w-none xl:flex-auto xl:px-16 xl:py-24">
								<figure className="relative isolate pt-6 sm:pt-12">
									<svg fill="none" viewBox="0 0 162 128" aria-hidden="true" className="absolute left-0 top-0 -z-10 h-32 stroke-white/20">
										<path
											d="M65.5697 118.507L65.8918 118.89C68.9503 116.314 71.367 113.253 73.1386 109.71C74.9162 106.155 75.8027 102.28 75.8027 98.0919C75.8027 94.237 75.16 90.6155 73.8708 87.2314C72.5851 83.8565 70.8137 80.9533 68.553 78.5292C66.4529 76.1079 63.9476 74.2482 61.0407 72.9536C58.2795 71.4949 55.276 70.767 52.0386 70.767C48.9935 70.767 46.4686 71.1668 44.4872 71.9924L44.4799 71.9955L44.4726 71.9988C42.7101 72.7999 41.1035 73.6831 39.6544 74.6492C38.2407 75.5916 36.8279 76.455 35.4159 77.2394L35.4047 77.2457L35.3938 77.2525C34.2318 77.9787 32.6713 78.3634 30.6736 78.3634C29.0405 78.3634 27.5131 77.2868 26.1274 74.8257C24.7483 72.2185 24.0519 69.2166 24.0519 65.8071C24.0519 60.0311 25.3782 54.4081 28.0373 48.9335C30.703 43.4454 34.3114 38.345 38.8667 33.6325C43.5812 28.761 49.0045 24.5159 55.1389 20.8979C60.1667 18.0071 65.4966 15.6179 71.1291 13.7305C73.8626 12.8145 75.8027 10.2968 75.8027 7.38572C75.8027 3.6497 72.6341 0.62247 68.8814 1.1527C61.1635 2.2432 53.7398 4.41426 46.6119 7.66522C37.5369 11.6459 29.5729 17.0612 22.7236 23.9105C16.0322 30.6019 10.618 38.4859 6.47981 47.558L6.47976 47.558L6.47682 47.5647C2.4901 56.6544 0.5 66.6148 0.5 77.4391C0.5 84.2996 1.61702 90.7679 3.85425 96.8404L3.8558 96.8445C6.08991 102.749 9.12394 108.02 12.959 112.654L12.959 112.654L12.9646 112.661C16.8027 117.138 21.2829 120.739 26.4034 123.459L26.4033 123.459L26.4144 123.465C31.5505 126.033 37.0873 127.316 43.0178 127.316C47.5035 127.316 51.6783 126.595 55.5376 125.148L55.5376 125.148L55.5477 125.144C59.5516 123.542 63.0052 121.456 65.9019 118.881L65.5697 118.507Z"
											id="b56e9dab-6ccb-4d32-ad02-6b4bb5d9bbeb"
										/>
										<use x={86} href="#b56e9dab-6ccb-4d32-ad02-6b4bb5d9bbeb" />
									</svg>
									<blockquote className="text-xl/8 font-semibold text-white sm:text-2xl/9">
										<p>
											“I've been in the tax consulting business for over 30 years, and I've honed my processes meticulously over the years. Incorporating this software into my
											workflow was a strategic decision, and it has exceeded my expectations. While I had efficient processes in place, this software has added an extra layer of
											organization and efficiency. The consultant dashboard and client profile management complement my existing systems seamlessly, allowing me to enhance my
											service delivery without disrupting what already works well.”
										</p>
									</blockquote>
									<figcaption className="mt-8 text-base">
										<div className="font-semibold text-white">Joyce Paquette, Joyce Paquette Tax Services</div>
										<div className="mt-1 text-gray-400">Windsor, Ontario</div>
									</figcaption>
								</figure>
							</div>
						</div>
					</div>
				</div>
			</div>

			<Footer />
		</div>
	)
}
